import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// Pages
import Home from './pages/Home';
import Features from './pages/Features';
import Pricing from './pages/Pricing';
import CaseStudies from './pages/CaseStudies';
import Contact from './pages/Contact';
import NotFound from './pages/NotFound';
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import Partners from './pages/auth/Partners';
import Callback from './pages/auth/Callback';
import Troubleshoot from './pages/auth/Troubleshoot';
import MagicLink from './pages/auth/MagicLink';

// Dashboard Pages
import Dashboard from './pages/dashboard/Dashboard';
import Overview from './pages/dashboard/Overview';
import CRM from './pages/dashboard/CRM';
import CRMTemplates from './pages/dashboard/CRMTemplates';
import CRMSettings from './pages/dashboard/CRMSettings';
import CRMIntegration from './pages/dashboard/CRMIntegration';
import Tasks from './pages/dashboard/Tasks';
import LeadCapture from './pages/dashboard/LeadCapture';
import LandingPages from './pages/dashboard/LandingPages';
import SocialHub from './pages/dashboard/SocialHub';
import EmailMarketing from './pages/dashboard/EmailMarketing';
import Reviews from './pages/dashboard/Reviews';
import Settings from './pages/dashboard/Settings';
import RSSFeedManager from './pages/dashboard/RSSFeedManager';
import AIVoiceAgents from './pages/dashboard/AIVoiceAgents';
import SlideDeckPreview from './pages/dashboard/landing-pages/SlideDeckPreview';
import TaxReturnSlideDeck from './pages/dashboard/landing-pages/TaxReturnSlideDeck';
import RiskNumberProspects from './pages/dashboard/RiskNumberProspects';

// Landing Pages
import LandingPagesRoutes from './pages/landing-pages';

// Components
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import PrivateRoute from './components/auth/PrivateRoute';
import Chatbot from './components/chatbot';

const queryClient = new QueryClient();

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <div className="flex flex-col min-h-screen">
          <Navbar />
          <main className="flex-grow">
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/features" element={<Features />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/case-studies" element={<CaseStudies />} />
              <Route path="/contact" element={<Contact />} />
              
              {/* Auth Routes */}
              <Route path="/auth/login" element={<Login />} />
              <Route path="/auth/register" element={<Register />} />
              <Route path="/auth/partners" element={<Partners />} />
              <Route path="/auth/callback" element={<Callback />} />
              <Route path="/auth/troubleshoot" element={<Troubleshoot />} />
              <Route path="/auth/magic-link" element={<MagicLink />} />
              
              {/* Protected Dashboard Routes */}
              <Route path="/dashboard" element={
                <PrivateRoute>
                  <Dashboard />
                </PrivateRoute>
              }>
                <Route index element={<Overview />} />
                <Route path="crm" element={<CRM />} />
                <Route path="crm/templates" element={<CRMTemplates />} />
                <Route path="crm/settings" element={<CRMSettings />} />
                <Route path="crm/integrations" element={<CRMIntegration />} />
                <Route path="tasks" element={<Tasks />} />
                <Route path="lead-capture/*" element={<LeadCapture />} />
                <Route path="landing-pages/*" element={<LandingPages />} />
                <Route path="social-hub" element={<SocialHub />} />
                <Route path="rss-feeds" element={<RSSFeedManager />} />
                <Route path="email-marketing/*" element={<EmailMarketing />} />
                <Route path="reviews" element={<Reviews />} />
                <Route path="settings" element={<Settings />} />
                <Route path="voice-agents" element={<AIVoiceAgents />} />
                <Route path="risk-number-prospects" element={<RiskNumberProspects />} />
              </Route>
              
              {/* Slide Deck Preview Route (outside of Dashboard layout) */}
              <Route path="/dashboard/slide-decks/:templateId" element={
                <PrivateRoute>
                  <SlideDeckPreview />
                </PrivateRoute>
              } />
              
              {/* Specific Slide Deck Routes */}
              <Route path="/dashboard/slide-decks/tax-return-qualification" element={
                <PrivateRoute>
                  <TaxReturnSlideDeck />
                </PrivateRoute>
              } />

              {/* Landing Page Routes */}
              <Route path="/l/*" element={<LandingPagesRoutes />} />
              
              <Route path="*" element={<NotFound />} />
            </Routes>
          </main>
          <Footer />
          <Toaster position="top-right" />
          <Chatbot />
        </div>
      </Router>
    </QueryClientProvider>
  );
}

export default App;